import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

const DashboardContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const WelcomeSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
`;

const WelcomeTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
`;

const WelcomeText = styled.p`
  font-size: 1rem;
  color: #6c757d;
`;


const QuickLinks = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const QuickLinksTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const LinkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`;

const LinkCard = styled.div`
  padding: 1.5rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
`;

const LinkIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
`;

const LinkLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #000000;
  margin-bottom: 0.25rem;
`;

const LinkCount = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
`;

interface DashboardStats {
  totalMembers: number;
  pendingApplications: number;
  thisWeekSchedules: number;
  unpaidFees: number;
  pendingGuests: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalMembers: 0,
    pendingApplications: 0,
    thisWeekSchedules: 0,
    unpaidFees: 0,
    pendingGuests: 0
  });
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  // 대시보드 통계 조회
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);

        // 전체 회원 수
        const membersResponse = await apiClient.get('/members/active');
        if (membersResponse.data.success) {
          setStats(prev => ({ ...prev, totalMembers: membersResponse.data.total || 0 }));
        }

        // 신규가입 신청 (대기)
        const applicationsResponse = await apiClient.get('/members/applications');
        if (applicationsResponse.data.success) {
          const pendingCount = applicationsResponse.data.data.filter((app: any) => app.status === 'R').length;
          setStats(prev => ({ ...prev, pendingApplications: pendingCount }));
        }

        // 이번 주 일정 (오늘부터 7일 내)
        const schedulesResponse = await apiClient.get('/schedules');
        if (schedulesResponse.data.success) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const nextWeek = new Date(today);
          nextWeek.setDate(today.getDate() + 7);

          const thisWeekSchedules = schedulesResponse.data.data.filter((s: any) => {
            const scheduleDate = new Date(s.schedule_date);
            scheduleDate.setHours(0, 0, 0, 0);
            return scheduleDate >= today && scheduleDate < nextWeek;
          }).length;

          setStats(prev => ({ ...prev, thisWeekSchedules }));
        }

        // 미납 회비 (현재 연도 기준)
        const feesResponse = await apiClient.get(`/fees/status/${currentYear}`);
        if (feesResponse.data.success) {
          const currentMonth = new Date().getMonth() + 1;
          const unpaidCount = feesResponse.data.data.filter((member: any) => {
            // 가입일 확인
            if (!member.registered_date) return false;
            
            const registeredDate = new Date(member.registered_date);
            const registeredYear = registeredDate.getFullYear();
            const registeredMonth = registeredDate.getMonth() + 1; // 1-12
            
            // 가입일 이전 달은 미납으로 카운트하지 않음
            let startMonth = 1;
            if (currentYear === registeredYear) {
              startMonth = registeredMonth; // 같은 연도면 가입 월부터
            } else if (currentYear > registeredYear) {
              startMonth = 1; // 가입 연도가 지났으면 1월부터
            } else {
              return false; // 가입 연도가 미래면 미납 아님
            }
            
            // 가입일 이후부터 현재 월까지 미납인 달이 있는지 확인
            for (let i = startMonth; i <= currentMonth; i++) {
              if (!member[`month_${i}`]) {
                return true; // 한 달이라도 미납이면 미납 회원
              }
            }
            return false;
          }).length;

          setStats(prev => ({ ...prev, unpaidFees: unpaidCount }));
        }

        // 게스트 신청 (대기)
        const guestsResponse = await apiClient.get('/guests');
        if (guestsResponse.data.success) {
          const pendingCount = guestsResponse.data.data.filter((g: any) => g.status === '대기').length;
          setStats(prev => ({ ...prev, pendingGuests: pendingCount }));
        }
      } catch (error) {
        console.error('대시보드 통계 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [currentYear]);

  if (loading) {
    return (
      <DashboardLayout title="관리자 대시보드">
        <DashboardContainer>
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>로딩 중...</div>
        </DashboardContainer>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="관리자 대시보드">
      <DashboardContainer>
        <WelcomeSection>
          <WelcomeTitle>안녕하세요, {user?.name}님 👋</WelcomeTitle>
          <WelcomeText>H_B 배구동호회 관리자 페이지입니다.</WelcomeText>
        </WelcomeSection>
        
        <QuickLinks>
          <QuickLinksTitle>빠른 메뉴</QuickLinksTitle>
          <LinkGrid>
            <LinkCard onClick={() => navigate('/admin/members')}>
              <LinkIcon>👥</LinkIcon>
              <LinkLabel>회원 명부</LinkLabel>
              <LinkCount>{stats.totalMembers}명</LinkCount>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/member-applications')}>
              <LinkIcon>📝</LinkIcon>
              <LinkLabel>신규가입 관리</LinkLabel>
              <LinkCount>{stats.pendingApplications}건</LinkCount>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/schedule')}>
              <LinkIcon>🏐</LinkIcon>
              <LinkLabel>운동 일정</LinkLabel>
              <LinkCount>{stats.thisWeekSchedules}건</LinkCount>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/fees')}>
              <LinkIcon>💰</LinkIcon>
              <LinkLabel>회비 관리</LinkLabel>
              <LinkCount>미납 {stats.unpaidFees}명</LinkCount>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/uniforms')}>
              <LinkIcon>👕</LinkIcon>
              <LinkLabel>유니폼 관리</LinkLabel>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/competitions')}>
              <LinkIcon>🏆</LinkIcon>
              <LinkLabel>대회 일정</LinkLabel>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/guests')}>
              <LinkIcon>👋</LinkIcon>
              <LinkLabel>게스트 관리</LinkLabel>
              <LinkCount>대기 {stats.pendingGuests}건</LinkCount>
            </LinkCard>
            <LinkCard onClick={() => navigate('/admin/notices')}>
              <LinkIcon>📢</LinkIcon>
              <LinkLabel>공지사항 관리</LinkLabel>
            </LinkCard>
          </LinkGrid>
        </QuickLinks>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default AdminDashboard;

